import React, { useRef } from 'react';
import ColorLensIcon from '@mui/icons-material/ColorLens';
import PopoverDialog from '@powerednow/components/Dialog/PopoverDialog';
import type { ThemeConfig } from '@powerednow/documentLayouts/themeConfig';
import { Slider, InputLabel } from '@mui/material';
import ColorPicker from '@powerednow/components/ColorPicker/ColorPicker';
import ComponentMatrix from '@powerednow/components/layout/ComponentMatrix';
import Box from '@powerednow/components/Box/Box';
import Button from '@powerednow/components/button/Button';
import useDependantState from '@powerednow/components/hooks/useDependantState';

function FontSizeEdit({
    currentValue, minValue, maxValue, onChange, label, unit, 
}) {
    const [fontSize, setFontSize] = useDependantState(currentValue, [currentValue]);

    const handleSliderChange = (event, newValue) => {
        setFontSize(newValue);
    };

    const handleSliderChangeCommitted = (event, newValue) => {
        if (onChange) {
            onChange(newValue);
        }
    };

    return (
        <Box sx={{ mb: 3 }}>
            <InputLabel>
                {label}
                :
                {fontSize}
                {unit}
            </InputLabel>
            <Slider
                color="secondary"
                value={fontSize}
                onChange={handleSliderChange}
                onChangeCommitted={handleSliderChangeCommitted}
                min={minValue}
                max={maxValue}
                step={0.5}
                valueLabelDisplay="auto"
            />
        </Box>
    );
}

function EditorContent({ themeConfig, themeConfigValues, onChange }: {
    themeConfig: ThemeConfig[];
    themeConfigValues: Record<string, any>;
    onChange: (key: string, value: any) => void;
}) {
    return (
        <div>
            {themeConfig && (themeConfig.map(configEntry => {
                if (configEntry.type === 'size') {
                    return (
                        <FontSizeEdit
                            unit={configEntry.unit}
                            label={configEntry.label}
                            key={configEntry.name}
                            currentValue={themeConfigValues[configEntry.name] || configEntry.defaultValue}
                            minValue={configEntry.minValue}
                            maxValue={configEntry.maxValue}
                            onChange={value => onChange(configEntry.name, value)}
                        />
                    );
                }
                if (configEntry.type === 'color') {
                    return (
                        <ColorPicker
                            key={configEntry.name}
                            label={configEntry.label}
                            value={themeConfigValues[configEntry.name] || configEntry.defaultValue}
                            onChange={value => onChange(configEntry.name, value)}
                        />
                    );
                }
                return null;
            }))}
        </div>
    );
}

export default function ThemeEditMenu({
    themeConfig, themeConfigValues, onChange, onSave, 
}: {
    themeConfig: ThemeConfig[];
    themeConfigValues: Record<string, any>;
    onChange?: (newConfig: Record<string, any>) => void;
    onSave: (newConfig: Record<string, any>) => void;
}) {
    const [currentConfig, setCurrentConfig] = useDependantState(themeConfigValues, [themeConfigValues]);
    const popoverRef = useRef(null);

    if (!themeConfig || themeConfig.length === 0) {
        return null;
    }

    const handleChange = (key, value) => {
        const newConfig = { ...currentConfig, [key]: value };
        setCurrentConfig(newConfig);
        if (onChange) {
            onChange(newConfig);
        }
    };

    const handleReset = () => {
        const newConfig = themeConfig.reduce((acc, configEntry) => {
            acc[configEntry.name] = configEntry.defaultValue;
            return acc;
        }, {});
        setCurrentConfig(newConfig);
        if (onChange) {
            onChange(newConfig);
        }
    };

    return (
        <PopoverDialog
            ref={popoverRef}
            buttonProps={{
                buttonType: 'warning',
                startIcon: <ColorLensIcon />,
            }}
        >
            <div>
                <h2>Theme Editor</h2>
                <Box>
                    <EditorContent 
                        themeConfig={themeConfig}
                        themeConfigValues={currentConfig}
                        onChange={handleChange}
                    />
                    <ComponentMatrix columns={2} spacing={5}>
                        <Button buttonType="secondary" onClick={handleReset}>Reset</Button>
                        <Button onClick={() => {
                            onSave(currentConfig);
                            if (popoverRef.current) {
                                popoverRef.current.close();
                            }
                        }}
                        >
                            Save
                        </Button>
                    </ComponentMatrix>
                </Box>
            </div>
        </PopoverDialog>
    );
}
